import { daysInMonth, MONTH_NAMES, toBikramSambat, toGregorian } from "@rigo/nepali-calendar";
import type { Calendar } from "@/api/types";
import { addDays, type IsoDate } from "./dates";

export type { Calendar };

/**
 * A month's filter value says what it means by itself, so the address bar and the query need no
 * calendar: "2026-08" is a Gregorian month, and "2026-07-17..2026-08-16" is a month of another
 * calendar, by the AD dates it covers. Fiscal years are written the same way.
 */
const SPAN = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;
const GREGORIAN = /^\d{4}-\d{2}$/;

export const isMonthValue = (value: string) => GREGORIAN.test(value) || SPAN.test(value);
export const isGregorianMonth = (value: string) => GREGORIAN.test(value);

/** The value of the month a date falls in. */
export function monthOf(iso: IsoDate, calendar: Calendar): string {
  if (calendar === "gregorian") return iso.slice(0, 7);
  const { year, month } = toBikramSambat(iso);
  return `${toGregorian(year, month, 1)}..${toGregorian(year, month, daysInMonth(year, month))}`;
}

/** The dates a month's value covers. */
export function monthRange(value: string): { from: IsoDate; to: IsoDate } {
  const span = SPAN.exec(value);
  if (span) return { from: span[1]!, to: span[2]! };
  const [year, number] = value.split("-").map(Number) as [number, number];
  const last = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return { from: `${value}-01`, to: `${value}-${String(last).padStart(2, "0")}` };
}

/** "Aug 2026", or "Shrawan 2083" for a month of the Bikram Sambat calendar. */
export function monthLabel(value: string): string {
  const span = SPAN.exec(value);
  if (span) {
    const { year, month } = toBikramSambat(span[1]!);
    return `${MONTH_NAMES[month - 1]} ${year}`;
  }
  return new Date(`${value}-01T00:00:00Z`).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Every month a fiscal year touches, for a month filter. */
export function monthsOf(
  year: { start: IsoDate; end: IsoDate },
  calendar: Calendar = "gregorian",
): string[] {
  const months: string[] = [];
  let on = year.start;
  while (on <= year.end) {
    const month = monthOf(on, calendar);
    months.push(month);
    on = addDays(monthRange(month).to, 1);
  }
  return months;
}
